import React, { useCallback, useMemo, useState } from 'react';

import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import CodeIcon from '@material-ui/icons/Code';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { OutlineButton } from '../../_core/_ui/buttons.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { useAvailableVariables } from '../../../lib/services/communication.service';
import {
  CommunicationContextType,
  ICommunicationVariable
} from '../../../lib/types/communication.types';
import { keepFieldFocusOnMouseDown } from '../ComposePanel/compose-panel.insert';
import { VARIABLE_GROUP_MESSAGE_KEYS } from './variable-panel.constants';
import { VariableFallbackPopover } from './variable-fallback-popover.component';
import { VariableRow } from './variable-row.component';
import { buildVariableToken, groupVariablesByNamespace } from './variable-panel.helpers';
import { getMessages } from './variable-panel.messages';
import { GroupGrid, PanelSurface, SectionHeaderRow, VariableGroupBlock } from './variable-panel.styles';

interface IVariablePanelProps {
  // The section heading ("Email message" / "SMS message") this panel's toggle
  // button sits beside — the panel owns the whole header row.
  sectionLabel: React.ReactNode;
  assessmentId: string;
  // True while the compose form has an assessment-session context row; gates
  // the session-scoped variable group (server enforces the effective catalog).
  hasSessionContext: boolean;
  onInsert: (token: string) => void;
}

export const VariablePanel: React.FC<IVariablePanelProps> = ({
  sectionLabel,
  assessmentId,
  hasSessionContext,
  onInsert
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);
  const [open, setOpen] = useState(false);
  // Skip the registry query until first expand — the keepMounted drawer renders
  // this panel on every page, and most sessions never open it.
  const [hasOpened, setHasOpened] = useState(false);
  const { variables, loading } = useAvailableVariables(hasOpened ? assessmentId : undefined);

  const [fallbackPrompt, setFallbackPrompt] = useState<{
    anchorEl: HTMLElement;
    variable: ICommunicationVariable;
  } | null>(null);

  const visibleVariables = useMemo(
    () => variables.filter(
      (variable) => hasSessionContext || variable.targetType !== CommunicationContextType.ASSESSMENT_SESSION
    ),
    [variables, hasSessionContext]
  );
  const groups = useMemo(() => groupVariablesByNamespace(visibleVariables), [visibleVariables]);

  const groupHeadingFor = (namespace: string): React.ReactNode => {
    const messageKey = VARIABLE_GROUP_MESSAGE_KEYS[namespace as keyof typeof VARIABLE_GROUP_MESSAGE_KEYS];
    return messageKey ? <IntlMsg {...messages[messageKey]} /> : namespace;
  };

  const handleToggle = useCallback(() => {
    setHasOpened(true);
    setOpen((wasOpen) => !wasOpen);
  }, []);

  const handleInsertClick = useCallback((variable: ICommunicationVariable, anchorEl: HTMLElement) => {
    if (variable.requiresFallback) {
      setFallbackPrompt({ anchorEl, variable });
      return;
    }
    onInsert(buildVariableToken(variable.key));
  }, [onInsert]);

  const handleFallbackConfirm = useCallback((fallback: string) => {
    if (fallbackPrompt) onInsert(buildVariableToken(fallbackPrompt.variable.key, fallback));
    setFallbackPrompt(null);
  }, [fallbackPrompt, onInsert]);

  return (
    <>
      <SectionHeaderRow>
        {sectionLabel}
        <OutlineButton
          size="small"
          aria-expanded={open}
          onMouseDown={keepFieldFocusOnMouseDown}
          onClick={handleToggle}
          startIcon={<CodeIcon fontSize="small" />}
          endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        >
          <IntlMsg {...messages.toggle} />
        </OutlineButton>
      </SectionHeaderRow>
      <Collapse in={open} unmountOnExit>
        <PanelSurface>
          <Typography variant="caption" color="textSecondary">
            <IntlMsg {...(loading && !variables.length ? messages.loading : messages.intro)} />
          </Typography>
          <GroupGrid>
            {groups.map((group) => (
              <VariableGroupBlock key={group.namespace}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  {groupHeadingFor(group.namespace)}
                </Typography>
                {group.variables.map((variable) => (
                  <VariableRow key={variable.key} variable={variable} onInsertClick={handleInsertClick} />
                ))}
              </VariableGroupBlock>
            ))}
          </GroupGrid>
        </PanelSurface>
      </Collapse>
      <VariableFallbackPopover
        anchorEl={fallbackPrompt ? fallbackPrompt.anchorEl : null}
        variableKey={fallbackPrompt ? fallbackPrompt.variable.key : undefined}
        onConfirm={handleFallbackConfirm}
        onClose={() => setFallbackPrompt(null)}
      />
    </>
  );
};
